import React, { createContext, useState } from 'react';
import { ExtractedSymptom } from '../components/llm-diagnose/LLMDiagnoseList';
import { ExtractedAnimal } from '../api/LLM';

export type LLMPatientInformation = {
  name: string;
  species: string;
  breed?: string;
  date_of_birth: string;
  weight_kg: number;
  gender: string;
  intact: boolean;
};

type LLMDiagnoseContextType = {
  freeText: string;
  setFreeText: (freeText: string) => void;
  patientInformation: LLMPatientInformation | null;
  setPatientInformation: (
    patientInformation: LLMPatientInformation | null
  ) => void;
  setDirectDiagnose: (directDiagnose: boolean) => void;
  directDiagnose: boolean;
  isActiveLLMDiagnoseFlow: boolean;
  setIsActiveLLMDiagnoseFlow: (isActiveLLMDiagnoseFlow: boolean) => void;
  loading: boolean;
  sanitizing: boolean;
  setLoading: (loading: boolean) => void;
  setSanitizing: (sanitizing: boolean) => void;
  extractedAnimal: ExtractedAnimal | null;
  setExtractedAnimal: (extractedAnimal: ExtractedAnimal | null) => void;
  extractedSymptoms: { [key: string]: ExtractedSymptom[] };
  setExtractedSymptoms: (extractedSymptoms: {
    [key: string]: ExtractedSymptom[];
  }) => void;
  error: string | null;
  setError: (error: string | null) => void;
};

const LLMDiagnoseContext = createContext<LLMDiagnoseContextType>({
  freeText: '',
  setFreeText: () => {},
  patientInformation: null,
  setPatientInformation: () => {},
  setDirectDiagnose: () => {},
  directDiagnose: false,
  isActiveLLMDiagnoseFlow: false,
  setIsActiveLLMDiagnoseFlow: () => {},
  extractedSymptoms: {},
  setExtractedSymptoms: () => {},
  extractedAnimal: null,
  setExtractedAnimal: () => {},
  loading: false,
  setLoading: () => {},
  error: '',
  setError: () => {},
  sanitizing: false,
  setSanitizing: () => {},
});

export const LLMDiagnoseProvider = ({ children }: React.PropsWithChildren) => {
  const [freeText, setFreeText] = useState('');
  const [patientInformation, setPatientInformation] =
    useState<LLMPatientInformation | null>(null);
  const [directDiagnose, setDirectDiagnose] = useState(false);
  const [isActiveLLMDiagnoseFlow, setIsActiveLLMDiagnoseFlow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sanitizing, setSanitizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractedAnimal, setExtractedAnimal] =
    useState<ExtractedAnimal | null>(null);
  const [extractedSymptoms, setExtractedSymptoms] = useState<{
    [key: string]: ExtractedSymptom[];
  }>({});
  const value = {
    freeText,
    setFreeText,
    patientInformation,
    setPatientInformation,
    setDirectDiagnose,
    directDiagnose,
    isActiveLLMDiagnoseFlow,
    setIsActiveLLMDiagnoseFlow,
    extractedSymptoms,
    setExtractedSymptoms,
    extractedAnimal,
    setExtractedAnimal,
    loading,
    setLoading,
    error,
    setError,
    sanitizing,
    setSanitizing,
  };
  return (
    <LLMDiagnoseContext.Provider value={value}>
      {children}
    </LLMDiagnoseContext.Provider>
  );
};

export const useLLMDiagnose = () => {
  return React.useContext(LLMDiagnoseContext);
};
